/**
 * Shared helpers for splitting API filter metadata into toolbar (`isDefault: true`)
 * vs modal-only fields — same contract as all-candidates.jsx / candidate-hire-status.jsx.
 */

/** Empty / whitespace-only filter values → null for API bodies. */
export const strOrNullFilterValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized === "" ? null : normalized;
};

/**
 * Normalize `<input type="date">` values for filter POST bodies.
 * Sends plain YYYY-MM-DD (same as call-back-request.jsx) — avoids local startOf/endOf
 * ISO conversion shifting the calendar day in UTC (e.g. IST → previous UTC date).
 */
export const normalizeMetadataFilterDateForApi = (value) => {
  if (value == null || value === "") return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (trimmed.includes("T")) {
    return trimmed.split("T")[0];
  }
  return trimmed;
};

const COMPLETION_DATE_FILTER_FIELD_FALLBACKS = {
  from: ["completionFromDate", "completionDateFrom"],
  to: ["completionToDate", "completionDateTo"],
};

const completionDateFilterLabelText = (filterDefinitionRow = {}) =>
  String(
    filterDefinitionRow?.title ||
      filterDefinitionRow?.placeholder ||
      filterDefinitionRow?.field ||
      ""
  ).trim();

/** Resolve Completion From / Completion To metadata `field` keys for candidate-style filters. */
export const resolveCompletionDateFilterFieldKeys = (metadataFilters = []) => {
  const dateFilterRows = normalizeMetadataFiltersArray(metadataFilters).filter(
    (filterDefinitionRow) =>
      String(filterDefinitionRow?.type || "").toLowerCase() === "date"
  );

  const findByTitlePattern = (pattern) =>
    dateFilterRows.find((filterDefinitionRow) =>
      pattern.test(completionDateFilterLabelText(filterDefinitionRow))
    );

  const fromDefinitionRow =
    findByTitlePattern(/completion\s+from/i) ||
    dateFilterRows.find((filterDefinitionRow) =>
      COMPLETION_DATE_FILTER_FIELD_FALLBACKS.from.includes(filterDefinitionRow?.field)
    );
  const toDefinitionRow =
    findByTitlePattern(/completion\s+to/i) ||
    dateFilterRows.find((filterDefinitionRow) =>
      COMPLETION_DATE_FILTER_FIELD_FALLBACKS.to.includes(filterDefinitionRow?.field)
    );

  return {
    fromFieldKey: fromDefinitionRow?.field || null,
    toFieldKey: toDefinitionRow?.field || null,
  };
};

/**
 * When both completion dates are set, `to` must not be before `from`.
 * Returns `{ valid: true }` or `{ valid: false, message }`.
 */
export const validateCompletionDateFilterRange = (
  form = {},
  metadataFilters = []
) => {
  const { fromFieldKey, toFieldKey } = resolveCompletionDateFilterFieldKeys(metadataFilters);
  if (!fromFieldKey || !toFieldKey) {
    return { valid: true };
  }

  const fromDateValue = normalizeMetadataFilterDateForApi(form?.[fromFieldKey]);
  const toDateValue = normalizeMetadataFilterDateForApi(form?.[toFieldKey]);
  if (!fromDateValue || !toDateValue) {
    return { valid: true };
  }

  if (toDateValue < fromDateValue) {
    return {
      valid: false,
      message: "Completion To Date must be greater than Completion From Date.",
    };
  }

  return { valid: true };
};

/** Call executive upper toolbar: col-md-4 + four col-md-2 (search uses last md-2). */
export const CALL_EXECUTIVE_TOOLBAR_COL_MD = [4, 2, 2, 2, 2];

/**
 * All Candidates / deactivate-candidate — two rows × 12 cols:
 * Row 1: search(4) + status(2) + order(3) + profile(3)
 * Row 2: client(2) + from(2) + to(2) + update(2) + fadv(2) + search btn(2)
 */
export const CANDIDATES_TOOLBAR_COL_MD = [4, 2, 3, 3, 2, 2, 2, 2, 2, 2];

const METADATA_FILTER_TYPE_COL_MD_BY_DENSITY = {
  text: { few: 4, medium: 4, many: 4 },
  dropdown: { few: 3, medium: 2, many: 2 },
  select: { few: 3, medium: 2, many: 2 },
  multiselect: { few: 4, medium: 3, many: 3 },
  date: { few: 2, medium: 2, many: 2 },
  autocomplete: { few: 3, medium: 3, many: 2 },
  asyncselect: { few: 3, medium: 3, many: 2 },
};

/** Dropdown fields with longer labels/placeholders need extra column width on dense toolbars. */
const METADATA_TOOLBAR_WIDE_DROPDOWN_FIELDS = new Set([
  "orderStatus",
  "profileStatus",
  "orderStatusIds",
  "profileStatusIds",
]);

const resolveMultiselectToolbarColumnMd = (definitionRow, densityBucket, listFilter = {}) => {
  const fieldKey = definitionRow?.field;
  const rawSelected = fieldKey ? listFilter?.[fieldKey] : null;
  const selectedCount = Array.isArray(rawSelected) ? rawSelected.length : 0;
  let columnMd =
    METADATA_FILTER_TYPE_COL_MD_BY_DENSITY.multiselect[densityBucket] ?? 3;
  if (METADATA_TOOLBAR_WIDE_DROPDOWN_FIELDS.has(fieldKey)) {
    columnMd = Math.max(columnMd, 3);
  }
  if (selectedCount >= 2) columnMd = Math.min(6, columnMd + 1);
  if (selectedCount >= 4) columnMd = Math.min(6, columnMd + 1);
  if (selectedCount >= 6) columnMd = Math.min(6, columnMd + 1);
  return columnMd;
};

const resolveFilterColumnMd = (definitionRow, densityBucket, listFilter = {}) => {
  const fieldKey = definitionRow?.field;
  const controlType = String(definitionRow?.type || "text").toLowerCase();
  const placeholderCopy = String(
    definitionRow?.placeholder || definitionRow?.title || ""
  ).trim();

  if (controlType === "multiselect") {
    return resolveMultiselectToolbarColumnMd(definitionRow, densityBucket, listFilter);
  }

  if (controlType === "text" || fieldKey === "search") {
    return METADATA_FILTER_TYPE_COL_MD_BY_DENSITY.text[densityBucket];
  }

  if (
    METADATA_TOOLBAR_WIDE_DROPDOWN_FIELDS.has(fieldKey) ||
    ((controlType === "dropdown" || controlType === "select") &&
      placeholderCopy.length > 14)
  ) {
    return densityBucket === "many" ? 3 : 3;
  }

  const widthByType =
    METADATA_FILTER_TYPE_COL_MD_BY_DENSITY[controlType] ||
    METADATA_FILTER_TYPE_COL_MD_BY_DENSITY.text;
  return widthByType[densityBucket];
};

const resolveMetadataToolbarDensityBucket = (filterCount) => {
  if (filterCount <= 3) return "few";
  if (filterCount <= 6) return "medium";
  return "many";
};

/**
 * Bootstrap `md` column widths per toolbar filter + trailing search column.
 * Adapts to filter count and control type (wider text/search, compact dropdowns/dates).
 */
export const computeMetadataToolbarColumnLayout = (
  toolbarDefinitions = [],
  { searchColMd = 2, layoutPreset = null, listFilter = {} } = {}
) => {
  if (layoutPreset === "callExecutive") {
    return [...CALL_EXECUTIVE_TOOLBAR_COL_MD];
  }

  const definitions = normalizeMetadataFiltersArray(toolbarDefinitions);
  if (!definitions.length) return [searchColMd];

  const densityBucket = resolveMetadataToolbarDensityBucket(definitions.length);
  const filterColumnWidths = definitions.map((definitionRow) =>
    resolveFilterColumnMd(definitionRow, densityBucket, listFilter)
  );

  return [...filterColumnWidths, searchColMd];
};

/**
 * `pageName` for POST `common/system-users` (async user filter select).
 * Maps table `pageName` props to API values when they differ (e.g. Candidates → Candidate).
 */
export const resolveSystemUsersFilterPageName = (tablePageName = "") => {
  const raw = String(tablePageName ?? "").trim();
  if (!raw) return "";
  const norm = raw.replace(/\s+/g, "").toLowerCase();
  const MAP = {
    candidates: "Candidate",
    allcandidates: "Candidate",
    candidatehirestatus: "Candidate",
  };
  return MAP[norm] ?? raw;
};

/**
 * `pageName` for POST V2 `common/client` (async client filter select).
 * Maps table / screen `pageName` props to API values when they differ.
 */
export const resolveAsyncClientFilterPageName = (tablePageName = "") => {
  const raw = String(tablePageName ?? "").trim();
  if (!raw) return "";
  const norm = raw.replace(/[\s_-]+/g, "").toLowerCase();
  const MAP = {
    // Candidate lists
    candidate: "Candidate",
    candidates: "Candidate",
    allcandidates: "Candidate",
    candidatestatus: "CandidateStatus",
    candidatetwo: "CandidateStatus",
    candidatehirestatus: "CandidateHireStatus",
    externalleads: "ExternalLeads",
    candidateonboarding: "CandidateOnboardingList",
    candidateonboardinglist: "CandidateOnboardingList",

    // Active-clients forms / uploads
    candidateformrestrict: "CandidateFormRestrict",
    newcandidate: "CandidateFormRestrict",

    verifytest: "VerifyTest",
    modifyclosure: "ModifyClosure",
    verifyfadvcsv: "VerifyFadvCsv",

    // FADV logs
    fadvlog: "FadvLog",
    fadvcsvlog: "FadvLog",
    fadvorderlog: "FadvLog",
    fadvprofileorderlog: "FadvLog",
    blockfadvrequestor: "FadvLog",

    // Call executive family
    callexecutive: "CallExecutive",
    callexecutiveschedule: "CallExecutive",
    callexecutivenotonai: "CallExecutive",
    callexecutivetoday: "CallExecutive",
    callexecutivevm: "CallExecutive",
    callexecutiveoldcall: "CallExecutive",
    callexecutivenoemail: "CallExecutive",
    callexecutivenondriver: "CallExecutive",
    callexecutiveinterview: "CallExecutive",
    callexecutiveunderage: "CallExecutive",
    callexecutivewrongnumber: "CallExecutive",
    callmanagerorderinprogress: "CallExecutive",
    callmanagerreadytosend: "CallExecutive",
    rtracknowledge: "CallExecutive",

    callmgrfr: "CallMgrFR",
    callmanagerfurtherreview: "CallMgrFR",

    mycall: "MyCall",
    callexecutivemycall: "MyCall",
    callexecutivedeactivatecandidate: "MyCall",

    // Client menu screens
    clientmenu: "ClientMenu",
    prequestioning: "ClientMenu",
    jobdescription: "ClientMenu",
    clientappreciation: "ClientMenu",
    clientappretiation: "ClientMenu",

    clientreview: "ClientReview",
    onboardingadd: "OnboardingAdd",
    onboarding: "OnboardingAdd",

    // Reports
    reportinprogress: "ReportInProgress",
    inprogressreport: "ReportInProgress",
    reportclienttype: "ReportClientType",
    clientreport: "ReportClientType",
    rtrinternalreport: "ReportClientType",

    reportfiltered: "ReportFiltered",
    eligiblereport: "ReportFiltered",
    closurereport: "ReportFiltered",
    clientuserreport: "ReportFiltered",
    candidatesummaryreport: "ReportFiltered",
    leadsaddedreport: "ReportFiltered",
    backendperformancereport: "ReportFiltered",
    compliancereport: "ReportFiltered",
    compliancyreport: "ReportFiltered",
    segmentcompletion: "ReportFiltered",
    segmentcompletionreport: "ReportFiltered",
    userclientactivityreport: "ReportFiltered",

    reportunrestricted: "ReportUnrestricted",
    clientstatusreport: "ReportUnrestricted",
    teamperformancereport: "ReportUnrestricted",
    readytosendreport: "ReportUnrestricted",

    activitylist: "ActivityList",
    useractivities: "ActivityList",

    airecruiterlog: "AIRecruiterLog",
    callaudit: "CallAudit",
  };
  return MAP[norm] ?? raw;
};

/**
 * `pageName` for POST V2 `common/team` (team filter dropdown).
 * Maps table / screen `pageName` props to API values when they differ.
 */
export const resolveAsyncTeamFilterPageName = (tablePageName = "") => {
  const raw = String(tablePageName ?? "").trim();
  if (!raw) return "";
  const norm = raw.replace(/[\s_-]+/g, "").toLowerCase();
  const MAP = {
    // Call executive family (scoped team list)
    callexecutive: "CallExecutive",
    callexecutiveschedule: "CallExecutive",
    callexecutivenotonai: "CallExecutive",
    callexecutivetoday: "CallExecutive",
    callexecutivevm: "CallExecutive",
    callexecutiveoldcall: "CallExecutive",
    callexecutivenoemail: "CallExecutive",
    callexecutivenondriver: "CallExecutive",
    callexecutiveinterview: "CallExecutive",
    callexecutiveunderage: "CallExecutive",
    callexecutivewrongnumber: "CallExecutive",
    callmanagerorderinprogress: "CallExecutive",
    callmanagerreadytosend: "CallExecutive",
    callmanagerfurtherreview: "CallExecutive",
    callmgrfr: "CallExecutive",
    rtracknowledge: "CallExecutive",

    // Reports — scoped team filter
    reportfiltered: "ReportFiltered",
    closurereport: "ReportFiltered",
    leadsaddedreport: "ReportFiltered",
    backendperformancereport: "ReportFiltered",
    sessionreport: "ReportFiltered",

    // Team Performance — all teams
    reportunrestricted: "ReportUnrestricted",
    teamperformancereport: "ReportUnrestricted",

    // Attendance — all active teams
    attendancereport: "AttendanceReport",
  };
  return MAP[norm] ?? raw;
};

/** Maps metadata `autoComplete.api` identifiers to async picker kinds. */
export const ASYNC_AUTOCOMPLETE_API_TO_KIND = {
  get_client_options: "client",
  get_candidate_options: "candidate",
  get_system_users_options: "user",
  get_source_options: "source",
};

/** UI-only companion key for persisting async select labels (e.g. `clientId` → `clientIdLabel`). */
export const getAsyncFilterLabelFieldKey = (fieldName) =>
  fieldName ? `${String(fieldName)}Label` : "";

export const isAsyncFilterLabelFieldKey = (fieldKey) =>
  typeof fieldKey === "string" && fieldKey.length > 0 && fieldKey.endsWith("Label");

export const isMetadataAsyncSelectFilterDefinition = (item = {}) => {
  const controlType = String(item?.type || "").toLowerCase();
  if (controlType === "asyncselect") {
    return Boolean(item?.asyncKind || item?.asynckind);
  }
  if (controlType === "autocomplete") {
    return Boolean(ASYNC_AUTOCOMPLETE_API_TO_KIND[item?.api]);
  }
  return false;
};

/** Standard `clientId` filter field used on many list/upload screens. */
export const CLIENT_ASYNC_FILTER_FIELD = "clientId";

export const getClientAsyncFilterLabelFieldKey = () =>
  getAsyncFilterLabelFieldKey(CLIENT_ASYNC_FILTER_FIELD);

/** `{ value, label }` for `AsyncClientSelect` when `clientId` + `clientIdLabel` are stored. */
export const buildClientAsyncFilterInitialOption = (clientId, clientIdLabel) =>
  buildAsyncFilterInitialOption(CLIENT_ASYNC_FILTER_FIELD, clientId, {
    [CLIENT_ASYNC_FILTER_FIELD]: clientId,
    [getClientAsyncFilterLabelFieldKey()]: clientIdLabel,
  });

/** Apply async client pick to a filter form object (`clientId` + `clientIdLabel`). */
export const applyClientAsyncFilterOptionToFormState = (previousFormState, selectedOption) => ({
  ...previousFormState,
  [CLIENT_ASYNC_FILTER_FIELD]: selectedOption?.value ?? "",
  [getClientAsyncFilterLabelFieldKey()]: selectedOption?.label ?? "",
});

/** Standard `userId` filter field used on many list screens. */
export const USER_ASYNC_FILTER_FIELD = "userId";

export const getUserAsyncFilterLabelFieldKey = () =>
  getAsyncFilterLabelFieldKey(USER_ASYNC_FILTER_FIELD);

/** `{ value, label }` for `AsyncUserSelect` when `userId` + `userIdLabel` are stored. */
export const buildUserAsyncFilterInitialOption = (userId, userIdLabel) =>
  buildAsyncFilterInitialOption(USER_ASYNC_FILTER_FIELD, userId, {
    [USER_ASYNC_FILTER_FIELD]: userId,
    [getUserAsyncFilterLabelFieldKey()]: userIdLabel,
  });

/** Apply async user pick to a filter form object (`userId` + `userIdLabel`). */
export const applyUserAsyncFilterOptionToFormState = (previousFormState, selectedOption) => ({
  ...previousFormState,
  [USER_ASYNC_FILTER_FIELD]: selectedOption?.value ?? "",
  [getUserAsyncFilterLabelFieldKey()]: selectedOption?.label ?? "",
});

/** Standard `candidateId` field for row-level candidate async selects. */
export const CANDIDATE_ASYNC_FILTER_FIELD = "candidateId";

export const getCandidateAsyncFilterLabelFieldKey = () =>
  getAsyncFilterLabelFieldKey(CANDIDATE_ASYNC_FILTER_FIELD);

/** `{ value, label }` for `AsyncCandidateSelect` when `candidateId` + `candidateIdLabel` are stored. */
export const buildCandidateAsyncFilterInitialOption = (candidateId, candidateIdLabel) =>
  buildAsyncFilterInitialOption(CANDIDATE_ASYNC_FILTER_FIELD, candidateId, {
    [CANDIDATE_ASYNC_FILTER_FIELD]: candidateId,
    [getCandidateAsyncFilterLabelFieldKey()]: candidateIdLabel,
  });

/** Build `{ value, label }` for AsyncSearchSelect when filter state stores a label companion field. */
export const buildAsyncFilterInitialOption = (fieldName, rawValue, listFilter = {}) => {
  const value = String(rawValue ?? "").trim();
  if (!value) return null;
  const label = String(listFilter?.[getAsyncFilterLabelFieldKey(fieldName)] ?? "").trim();
  if (!label) return null;
  return { value, label };
};

/** Apply async pick for any field (`fieldName` + `fieldNameLabel`). */
export const applyAsyncFilterOptionToFormState = (
  previousFormState,
  fieldName,
  selectedOption
) => ({
  ...previousFormState,
  [fieldName]: selectedOption?.value ?? "",
  [getAsyncFilterLabelFieldKey(fieldName)]: selectedOption?.label ?? "",
});

export const isMetadataFilterDefault = (filterDefinitionRow = {}) => {
  const rawIsDefaultValue =
    filterDefinitionRow?.isDefault ?? filterDefinitionRow?.isdefault;
  if (rawIsDefaultValue === true) return true;
  if (
    rawIsDefaultValue === false ||
    rawIsDefaultValue === null ||
    rawIsDefaultValue === undefined
  ) {
    return false;
  }
  const normalizedStringValue = String(rawIsDefaultValue).trim().toLowerCase();
  return normalizedStringValue === "true" || normalizedStringValue === "1";
};

export const buildEmptyFilterFromMeta = (filters = []) => {
  const obj = {};
  (Array.isArray(filters) ? filters : []).forEach((filterDefinitionRow) => {
    const field = filterDefinitionRow?.field;
    if (!field) return;
    const filterType = String(filterDefinitionRow?.type || "").toLowerCase();
    if (filterType === "date") {
      obj[field] = null;
    } else if (filterType === "multiselect") {
      obj[field] = [];
    } else {
      obj[field] = "";
    }
    if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
      obj[getAsyncFilterLabelFieldKey(field)] = "";
    }
  });
  return obj;
};

export const normalizeMetadataFiltersArray = (filters) =>
  (Array.isArray(filters) ? filters : []).filter((row) => row?.field);

export const splitMetadataFiltersByDefault = (filters) => {
  const normalized = normalizeMetadataFiltersArray(filters);
  return {
    all: normalized,
    toolbar: normalized.filter(isMetadataFilterDefault),
    modalOnly: normalized.filter((row) => !isMetadataFilterDefault(row)),
    usesMetadataDrivenFilterUi: normalized.length > 0,
  };
};

export const getMetadataFilterUiFlags = (modalOnlyDefinitions, usesMetadataDrivenFilterUi) => ({
  shouldShowTableHeaderFilterAndResetControls:
    !usesMetadataDrivenFilterUi || modalOnlyDefinitions.length > 0,
  shouldRenderMetadataToolbarFilterRow: false, // set by caller from toolbar length
});

/** Map metadata definitions + current form state → AllCommonFilterModal `filterData` rows. */
export const mapFilterDefinitionsToModalData = (definitions = [], listFilter = {}) =>
  (Array.isArray(definitions) ? definitions : []).map((item) => {
    const fieldName = item?.field;
    const rawValue = listFilter?.[fieldName];
    const filterType = String(item?.type || "").toLowerCase();
    const normalizedValue =
      typeof rawValue === "boolean"
        ? String(rawValue)
        : filterType === "multiselect"
          ? Array.isArray(rawValue)
            ? rawValue
            : []
          : rawValue ?? (filterType === "date" ? null : "");
    return {
      title: item?.title || fieldName,
      field: fieldName,
      type: item?.type || "text",
      placeholder: item?.placeholder || item?.title || "",
      api: item?.api || null,
      asyncKind: item?.asyncKind || item?.asynckind || undefined,
      itemArray:
        item?.itemArray ?? item?.options ?? item?.OptionItems ?? undefined,
      value: normalizedValue,
      initialOption: isMetadataAsyncSelectFilterDefinition(item)
        ? buildAsyncFilterInitialOption(fieldName, normalizedValue, listFilter)
        : null,
    };
  });

/** Whether a single filter field value counts as "applied" (non-empty). */
export const isMetadataFilterValueActive = (value, filterType) => {
  const type = String(filterType || "").toLowerCase();
  if (type === "toggle" || type === "switch" || type === "checkbox") {
    return value === true;
  }
  if (type === "multiselect") {
    return Array.isArray(value) && value.length > 0;
  }
  if (type === "date") {
    return value != null && String(value).trim() !== "";
  }
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  return Boolean(value);
};

/** Col wrapper classes for upper table filters; adds `--active` when a value is selected. */
export const buildUpperFilterFieldColClassName = (
  filterType = "text",
  fieldKey = "",
  isActive = false
) => {
  const normalizedType = String(filterType || "text").toLowerCase();
  const normalizedFieldKey = String(fieldKey ?? "").trim();
  return [
    "metadata-filter-field",
    `metadata-filter-field--${normalizedType}`,
    normalizedFieldKey ? `metadata-filter-field--field-${normalizedFieldKey}` : "",
    isActive ? "metadata-filter-field--active" : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/** Count how many metadata filter fields in `filterDefinitions` have active values in `listFilter`. */
export const countActiveMetadataFilters = (listFilter = {}, filterDefinitions = []) => {
  const definitions = normalizeMetadataFiltersArray(filterDefinitions);
  if (!definitions.length) return 0;
  let count = 0;
  definitions.forEach((definitionRow) => {
    const fieldName = definitionRow?.field;
    if (!fieldName) return;
    const rawValue = listFilter?.[fieldName];
    if (isMetadataFilterValueActive(rawValue, definitionRow?.type)) {
      count += 1;
    }
  });
  return count;
};

/** Merge modal submit values into existing list filter (toolbar defaults preserved). */
export const mergeModalSubmitIntoListFilter = (
  previousListFilter,
  modalFilterDataRows,
  modalFormPayload = {}
) => {
  const merged = { ...previousListFilter };
  (Array.isArray(modalFilterDataRows) ? modalFilterDataRows : []).forEach((row) => {
    const fieldName = row?.field;
    if (!fieldName) return;
    const filterType = String(row?.type || "").toLowerCase();
    const fallbackValue =
      filterType === "date" ? null : filterType === "multiselect" ? [] : "";
    merged[fieldName] = modalFormPayload?.[fieldName] ?? fallbackValue;
    if (isMetadataAsyncSelectFilterDefinition(row)) {
      const labelFieldKey = getAsyncFilterLabelFieldKey(fieldName);
      merged[labelFieldKey] = modalFormPayload?.[labelFieldKey] ?? "";
    }
  });
  return merged;
};

export const FILTER_PREFS_SETTINGS_KEY_PREFIX = "ats_filter_prefs_";

export const getFilterPreferencesStorageKey = (pageKey = "") => {
  const normalized = String(pageKey ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return `${FILTER_PREFS_SETTINGS_KEY_PREFIX}${normalized || "default"}`;
};

export const buildDefaultToolbarFieldOrderFromMetadata = (filters = []) =>
  normalizeMetadataFiltersArray(filters)
    .filter(isMetadataFilterDefault)
    .map((row) => row.field);

export const buildDefaultAdvancedFieldOrderFromMetadata = (filters = []) =>
  normalizeMetadataFiltersArray(filters)
    .filter((row) => !isMetadataFilterDefault(row))
    .map((row) => row.field);

export const buildDefaultFilterPreferencesFromMetadata = (filters = []) => ({
  toolbarFieldOrder: buildDefaultToolbarFieldOrderFromMetadata(filters),
  advancedFieldOrder: buildDefaultAdvancedFieldOrderFromMetadata(filters),
});

export const parseFilterPreferencesSettingsValue = (settingsValue) => {
  let parsed = settingsValue;

  if (typeof settingsValue === "string") {
    try {
      parsed = JSON.parse(settingsValue);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  const toolbarFieldOrder = Array.isArray(parsed.toolbarFieldOrder)
    ? parsed.toolbarFieldOrder.map((field) => String(field ?? "").trim()).filter(Boolean)
    : [];
  const advancedFieldOrder = Array.isArray(parsed.advancedFieldOrder)
    ? parsed.advancedFieldOrder.map((field) => String(field ?? "").trim()).filter(Boolean)
    : [];

  if (!toolbarFieldOrder.length && !advancedFieldOrder.length) return null;

  return { toolbarFieldOrder, advancedFieldOrder };
};

export const mergeFilterFieldOrderWithDefinitions = (
  previousOrder = [],
  filters = []
) => {
  const incomingFields = normalizeMetadataFiltersArray(filters).map((row) => row.field);
  if (!Array.isArray(previousOrder) || !previousOrder.length) {
    return incomingFields;
  }
  const retainedOrder = previousOrder.filter((field) => incomingFields.includes(field));
  incomingFields.forEach((field) => {
    if (!retainedOrder.includes(field)) {
      retainedOrder.push(field);
    }
  });
  return retainedOrder;
};

export const resolveMetadataFiltersWithPreferences = (
  filters = [],
  storedPreferences = null
) => {
  const normalized = normalizeMetadataFiltersArray(filters);
  if (!normalized.length) {
    return {
      all: [],
      toolbar: [],
      modalOnly: [],
      toolbarFieldOrder: [],
      advancedFieldOrder: [],
      usesMetadataDrivenFilterUi: false,
    };
  }

  const defaults = buildDefaultFilterPreferencesFromMetadata(normalized);
  const allFields = normalized.map((row) => row.field);
  let toolbarFieldOrder = [
    ...(storedPreferences?.toolbarFieldOrder ?? defaults.toolbarFieldOrder),
  ];
  let advancedFieldOrder = [
    ...(storedPreferences?.advancedFieldOrder ?? defaults.advancedFieldOrder),
  ];

  toolbarFieldOrder = toolbarFieldOrder.filter((field) => allFields.includes(field));
  advancedFieldOrder = advancedFieldOrder.filter(
    (field) => allFields.includes(field) && !toolbarFieldOrder.includes(field)
  );

  normalized.forEach((row) => {
    const field = row.field;
    if (!field) return;
    if (toolbarFieldOrder.includes(field) || advancedFieldOrder.includes(field)) return;
    if (isMetadataFilterDefault(row)) {
      toolbarFieldOrder.push(field);
    } else {
      advancedFieldOrder.push(field);
    }
  });

  const definitionByField = Object.fromEntries(
    normalized.map((row) => [row.field, row])
  );
  const toolbar = toolbarFieldOrder
    .map((field) => definitionByField[field])
    .filter(Boolean);
  const modalOnly = advancedFieldOrder
    .map((field) => definitionByField[field])
    .filter(Boolean);

  return {
    all: [...toolbar, ...modalOnly],
    toolbar,
    modalOnly,
    toolbarFieldOrder,
    advancedFieldOrder,
    usesMetadataDrivenFilterUi: true,
  };
};

export const applyFilterPreferenceUserSettingToStorage = (
  settingsKey = "",
  settingsValue = null
) => {
  const key = String(settingsKey ?? "").trim();
  if (!key.startsWith(FILTER_PREFS_SETTINGS_KEY_PREFIX)) return false;

  const preferences = parseFilterPreferencesSettingsValue(settingsValue);
  if (!preferences) return false;

  try {
    localStorage.setItem(key, JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
};

export const loadFilterPreferencesFromStorage = (pageKey = "") => {
  try {
    const raw = localStorage.getItem(getFilterPreferencesStorageKey(pageKey));
    if (!raw) return null;
    return parseFilterPreferencesSettingsValue(raw);
  } catch {
    return null;
  }
};

export const saveFilterPreferencesToStorage = (pageKey = "", preferences = {}) => {
  try {
    localStorage.setItem(
      getFilterPreferencesStorageKey(pageKey),
      JSON.stringify({
        toolbarFieldOrder: Array.isArray(preferences.toolbarFieldOrder)
          ? preferences.toolbarFieldOrder
          : [],
        advancedFieldOrder: Array.isArray(preferences.advancedFieldOrder)
          ? preferences.advancedFieldOrder
          : [],
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
};

export const reorderFilterFieldOrder = (fieldOrder = [], sourceIndex, destinationIndex) => {
  const next = [...fieldOrder];
  if (
    sourceIndex < 0 ||
    destinationIndex < 0 ||
    sourceIndex >= next.length ||
    destinationIndex >= next.length
  ) {
    return next;
  }
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next;
};

export const moveFilterFieldBetweenLists = (
  preferences = {},
  { field, sourceListId, destinationListId, sourceIndex, destinationIndex }
) => {
  const toolbarFieldOrder = [...(preferences.toolbarFieldOrder || [])];
  const advancedFieldOrder = [...(preferences.advancedFieldOrder || [])];

  if (sourceListId === destinationListId) {
    const reordered = reorderFilterFieldOrder(
      sourceListId === "toolbar-filters" ? toolbarFieldOrder : advancedFieldOrder,
      sourceIndex,
      destinationIndex
    );
    return {
      toolbarFieldOrder:
        sourceListId === "toolbar-filters" ? reordered : toolbarFieldOrder,
      advancedFieldOrder:
        sourceListId === "advanced-filters" ? reordered : advancedFieldOrder,
    };
  }

  const nextSource = [
    ...(sourceListId === "toolbar-filters" ? toolbarFieldOrder : advancedFieldOrder),
  ];
  const nextDestination = [
    ...(destinationListId === "toolbar-filters" ? toolbarFieldOrder : advancedFieldOrder),
  ];

  if (sourceIndex < 0 || sourceIndex >= nextSource.length) {
    return { toolbarFieldOrder, advancedFieldOrder };
  }

  const [movedField] = nextSource.splice(sourceIndex, 1);
  const resolvedField = field || movedField;
  if (!resolvedField) {
    return { toolbarFieldOrder, advancedFieldOrder };
  }

  const insertIndex = Math.max(0, Math.min(destinationIndex, nextDestination.length));
  nextDestination.splice(insertIndex, 0, resolvedField);

  return {
    toolbarFieldOrder:
      destinationListId === "toolbar-filters"
        ? nextDestination
        : sourceListId === "toolbar-filters"
          ? nextSource
          : toolbarFieldOrder,
    advancedFieldOrder:
      destinationListId === "advanced-filters"
        ? nextDestination
        : sourceListId === "advanced-filters"
          ? nextSource
          : advancedFieldOrder,
  };
};
